// CATALOG-DATA-132 — optional nutrition overlay (same-origin).
import type { CatalogV2Product } from "./catalog-v2-types.js";
import { fetchSameOriginJsonFile } from "./catalog-static-json-v2.js";
import { normalizeIdentifier } from "./product-identity-v2.js";
import {
  EMPTY_NUTRITION_V2,
  type NutritionBasis,
  type NutritionSource,
  type ProductNutritionV2,
} from "./nutrition-v2.js";

export type NutritionOverlayRow = Partial<ProductNutritionV2> & {
  nutrition_basis?: NutritionBasis | null;
  nutrition_source?: NutritionSource | null;
};

export type NutritionOverlayFile = {
  schema?: string;
  products?: Record<string, NutritionOverlayRow>;
};

let cache: Promise<NutritionOverlayFile> | null = null;

function isNutritionOverlayFile(json: unknown): json is NutritionOverlayFile {
  return Boolean(json && typeof json === "object" && (json as NutritionOverlayFile).products);
}

async function loadNutritionOverlay(): Promise<NutritionOverlayFile> {
  if (!cache) {
    cache = (async () => {
      const file = await fetchSameOriginJsonFile(
        "catalog-nutrition-overlay-v1.json",
        isNutritionOverlayFile,
      );
      return file ?? { products: {} };
    })();
  }
  return cache;
}

function mergeNutrition(row: NutritionOverlayRow | null | undefined): ProductNutritionV2 | null {
  if (!row) return null;
  const n: ProductNutritionV2 = { ...EMPTY_NUTRITION_V2, ...row };
  const hasAny =
    n.energy_kcal != null ||
    n.energy_kj != null ||
    n.protein_g != null ||
    n.fat_g != null ||
    n.carbohydrate_g != null ||
    n.sugars_g != null ||
    n.fiber_g != null ||
    n.salt_g != null ||
    n.saturated_fat_g != null ||
    Boolean(n.serving_size);
  return hasAny ? n : null;
}

export function applyNutritionOverlayToProduct(
  product: CatalogV2Product,
  row: NutritionOverlayRow | null | undefined,
): CatalogV2Product {
  const nutrition = mergeNutrition(row);
  if (!nutrition) return product;
  return { ...product, nutrition };
}

export async function enrichCatalogWithNutritionOverlay(
  products: CatalogV2Product[],
): Promise<CatalogV2Product[]> {
  const file = await loadNutritionOverlay();
  const map = file.products ?? {};
  return products.map((p) =>
    applyNutritionOverlayToProduct(p, map[p.id] ?? map[normalizeIdentifier(p.id)]),
  );
}

export function resetNutritionOverlayCacheForTests(): void {
  cache = null;
}
